import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { getFileName } from '../../utils/imageUtils';
import { handleExport } from '../../services/exportService';
import { analyzeTranscription, addLearnedWord } from '../../services/speechToISOService';
import VoiceInput from '../common/VoiceInput';
import VocabularyLearner from '../common/VocabularyLearner';
import ImageUpload from '../common/ImageUpload';
import AccordionSection from '../common/AccordionSection';

// Local state textarea to prevent re-render on each keystroke
const LocalTextarea = ({ value, onChange, placeholder, className, rows = 2 }) => {
    const [localValue, setLocalValue] = useState(value || '');

    return (
        <textarea
            placeholder={placeholder}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() => onChange(localValue)}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className={className}
            rows={rows}
        />
    );
};

export default function CompletionPanel({
    permit,
    onClose,
    onComplete,
    canComplete,
    completionChecks,
    setCompletionChecks,
    instructionAnswer,
    setInstructionAnswer,
    instructionFeedback,
    setInstructionFeedback,
    completionFeedback,
    setCompletionFeedback,
    conditionScore,
    setConditionScore,
    isReplacementConfirmed,
    setIsReplacementConfirmed,
    canCompleteReason,
    isoLibrary,
    onExecutionUpload
}) {
    const panelRef = useRef(null);
    const { adminData, locationData, systemSettings, updateVocabulary } = useData();
    const { user } = useAuth();
    const [exporting, setExporting] = useState(null); // 'pdf' or 'cmms'

    // Speech-to-ISO analysis state
    const [isoAnalysis, setIsoAnalysis] = useState(null);
    const [showLearner, setShowLearner] = useState(false);
    const prevFeedbackTextRef = useRef('');
    const vocabulary = systemSettings?.voice_vocabulary;

    // Auto-analyze when feedbackText changes (after voice transcription)
    useEffect(() => {
        const currentText = completionFeedback?.feedbackText || '';
        const prevText = prevFeedbackTextRef.current;

        // Only analyze if text actually changed and we have vocabulary
        if (currentText && currentText !== prevText && vocabulary) {
            const result = analyzeTranscription(currentText, vocabulary);
            setIsoAnalysis(result);

            // Auto-fill ISO fields only if they are currently empty (don't override manual selections)
            if (result.confidence > 0) {
                setCompletionFeedback(prev => {
                    const updates = { ...prev };
                    let hasAnyISO = false;

                    if (result.component && !prev.isoComponent) {
                        updates.isoComponent = result.component;
                        hasAnyISO = true;
                    }
                    if (result.failureMode && !prev.isoMode) {
                        updates.isoMode = result.failureMode;
                        hasAnyISO = true;
                    }
                    if (result.cause && !prev.isoCause) {
                        updates.isoCause = result.cause;
                        hasAnyISO = true;
                    }
                    // Auto-enable failure checkbox if ISO data was found
                    if (hasAnyISO && !prev.isFailure) {
                        updates.isFailure = true;
                    }

                    return updates;
                });
            }

            // Show learner if there are unmatched words
            if (result.unmatchedWords.length > 0) {
                setShowLearner(true);
            }
        }
        prevFeedbackTextRef.current = currentText;
    }, [completionFeedback?.feedbackText, vocabulary, setCompletionFeedback]);

    // Handle learning a new word
    const handleLearnWord = useCallback((word, category, isoValue) => {
        if (!vocabulary) return;
        const newVocab = addLearnedWord(vocabulary, category, word, isoValue);
        updateVocabulary(newVocab);

        // Re-analyze with updated vocabulary
        const text = completionFeedback?.feedbackText || '';
        if (text) {
            const result = analyzeTranscription(text, newVocab);
            setIsoAnalysis(result);

            // Auto-fill newly matched fields
            if (result.confidence > 0) {
                setCompletionFeedback(prev => {
                    const updates = { ...prev };
                    if (result.component && !prev.isoComponent) updates.isoComponent = result.component;
                    if (result.failureMode && !prev.isoMode) updates.isoMode = result.failureMode;
                    if (result.cause && !prev.isoCause) updates.isoCause = result.cause;
                    return updates;
                });
            }
        }
    }, [vocabulary, updateVocabulary, completionFeedback?.feedbackText, setCompletionFeedback]);

    // Export handlers
    const handleExportPDF = async () => {
        setExporting('pdf');
        try {
            const checklist = permit.executionChecklist || {};
            const completionData = {
                conditionScore,
                instructionAnswer,
                instructionFeedback,
                ...completionFeedback,
                ...completionChecks
            };
            await handleExport('pdf', permit, checklist, completionData, user?.uid);
            alert('PDF succesvol gedownload!');
        } catch (err) {
            alert('PDF export mislukt: ' + err.message);
        } finally {
            setExporting(null);
        }
    };

    const handleExportCMMS = async () => {
        setExporting('cmms');
        try {
            const checklist = permit.executionChecklist || {};
            const completionData = {
                conditionScore,
                instructionAnswer,
                instructionFeedback,
                ...completionFeedback,
                ...completionChecks
            };
            await handleExport('cmms', permit, checklist, completionData, user?.uid);
            alert('CMMS export succesvol gedownload!');
        } catch (err) {
            alert('CMMS export mislukt: ' + err.message);
        } finally {
            setExporting(null);
        }
    };

    // ISO 14224 Dutch Translation Map
    const isoTranslationMap = {
        "Fails to start": "Start niet", "Overheating": "Oververhitting", "Vibration": "Trillingen", "Noise": "Geluidshinder",
        "Leakage (Internal)": "Interne lekkage", "Leakage (Seal)": "Lekkage (Afdichting)", "Low Pressure": "Lage druk",
        "No Flow": "Geen flow", "Cavitation Noise": "Cavitatie geluid", "Breakdown": "Volledige defect",
        "Wear": "Slijtage", "Corrosion": "Corrosie", "Loose Part": "Los onderdeel", "Adjustment Error": "Instelfout",
        "Bearing": "Lager", "Winding": "Wikkeling", "Fan": "Ventilator", "Casing": "Behuizing",
        "Terminal Box": "Klemmenkast", "Mechanical Seal": "Mechanische afdichting", "Impeller": "Waaier",
        "Shaft": "As", "General Component": "Algemeen component", "Fastener": "Bevestigingsmateriaal",
        "Sensor": "Sensor", "Frame": "Frame/Gestel", "Actuator": "Aandrijving/Actuator", "Valve Body": "Afsluiterhuis",
        "Seat": "Zitting", "Stem": "Spindel", "Seal": "Afdichting", "Belt": "Transportband",
        "Roller": "Rol", "Drive Pulley": "Aandrijftrommel", "Tail Pulley": "Keertrommel", "Scraper": "Schraper",
        "Sensing Element": "Sensor-element", "Transmitter": "Transmitter", "Cable/Connector": "Kabel/Connector",
        "Mounting Bracket": "Montagebeugel", "Protection Sleeve": "Beschermhuls", "Piston/Screw": "Zuiger/Schroef",
        "Valve (Suction/Discharge)": "Klep (Zuig/Pers)", "Cooler": "Koeler", "Lubrication System": "Smeersysteem",
        "Tube Bundle": "Buizenbundel", "Shell": "Mantel", "Gasket": "Pakking", "Connection/Flange": "Aansluiting/Flens",
        "Plate": "Plaat", "External Leakage": "Externe Lekkage", "Internal Leakage": "Interne Lekkage",
        "Fail to Open": "Opent niet", "Fail to Close": "Sluit niet", "Clugging/Plugging": "Verstopping",
        "Belt Slippage": "Bandslip", "Belt Tear/Damage": "Bandschade/Scheur", "Roller Seizure": "Rol vastgelopen",
        "Misalignment/Tracking": "Scheefloop", "Drive Failure": "Aandrijfstoring", "No Signal": "Geen signaal",
        "Erroneous Signal": "Foutief signaal", "Drift": "Verloop/Drift", "Frozen Signal": "Bevroren signaal",
        "Short Circuit": "Kortsluiting", "High Temperature": "Hoge Temperatuur", "Fail to Start": "Start niet",
        "Oil Leakage": "Olielekkage", "Internal Leakage (Cross)": "Interne Lekkage (Kruis)", "Fouling/Scaling": "Vervuiling/Aanslag",
        "High Pressure Drop": "Hoog drukverschil", "Tube Failure": "Buisdefect", "Lubrication": "Smering",
        "Misalignment": "Uitlijning", "Overload": "Overbelasting", "Unknown": "Onbekend"
    };

    const handleCompletionCheck = (key) => setCompletionChecks(prev => ({ ...prev, [key]: !prev[key] }));

    return (
        <AccordionSection
            title="Afmeld Check & Registratie"
            icon="✅"
            isOpen={true}
            isComplete={canComplete}
            className="mt-8"
        >
            <div className="space-y-8 mt-4">
                {/* 1. Administration */}
                <div className="space-y-4">
                    <div className="text-[10px] text-slate-500 uppercase font-black tracking-widest pl-1">Administratie & Hygiëne</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                            { key: 'ultimoIngevuld', label: 'Ultimo Job ingevuld?' },
                            { key: 'werkplekSchoon', label: 'Werkplek schoon (5S)?' },
                            { key: 'onderdelenAfgeboekt', label: 'Onderdelen afgeboekt?' }
                        ].map(check => {
                            const isChecked = !!completionChecks[check.key];
                            return (
                                <label key={check.key} className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all shadow-lg ${isChecked ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600 hover:bg-slate-800'
                                    }`}>
                                    <input
                                        type="checkbox"
                                        className="w-6 h-6 rounded border-slate-500 text-green-600 bg-slate-900 mt-1 cursor-pointer shrink-0"
                                        checked={isChecked}
                                        onChange={() => handleCompletionCheck(check.key)}
                                    />
                                    <span className="text-sm font-bold text-white leading-snug">{check.label}</span>
                                </label>
                            );
                        })}
                    </div>

                    <div className={`p-5 rounded-xl border-2 transition-all shadow-xl ${instructionAnswer ? 'bg-slate-800/80 border-slate-700' : 'bg-blue-900/10 border-blue-500/30 ring-1 ring-blue-500/20'}`}>
                        <div className="flex flex-col gap-4 p-5 bg-slate-900/40 rounded-xl border border-slate-700 shadow-inner">
                            <span className="text-sm font-black text-white uppercase tracking-tight">Klopte de werkinstructie?</span>
                            <div className="flex gap-4">
                                {(() => {
                                    const isYes = instructionAnswer === 'yes';
                                    const isNo = instructionAnswer === 'no';
                                    return (
                                        <>
                                            <button
                                                onClick={() => setInstructionAnswer('yes')}
                                                className={`flex-1 px-8 py-4 rounded-xl text-lg font-black transition-all shadow-lg border-2 ${isYes
                                                    ? 'border-white/50 text-black scale-105 shadow-xl'
                                                    : 'bg-white text-black border-slate-300 hover:bg-slate-50'}`}
                                                style={isYes ? { backgroundColor: '#22c55e' } : {}}
                                            >
                                                JA
                                            </button>
                                            <button
                                                onClick={() => setInstructionAnswer('no')}
                                                className={`flex-1 px-8 py-4 rounded-xl text-lg font-black transition-all shadow-lg border-2 ${isNo
                                                    ? 'border-white/50 text-white scale-105 shadow-xl'
                                                    : 'bg-white text-black border-slate-300 hover:bg-slate-50'}`}
                                                style={isNo ? { backgroundColor: '#ef4444' } : {}}
                                            >
                                                NEE
                                            </button>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                        {instructionAnswer === 'no' && (
                            <VoiceInput
                                value={instructionFeedback}
                                onChange={(e) => setInstructionFeedback(e.target.value)}
                                placeholder="Wat klopte er niet?"
                                className="w-full bg-white border-2 border-red-500/50 rounded-xl p-4 text-sm text-black font-medium"
                                minHeight="h-24"
                            />
                        )}
                    </div>
                </div>

                {/* 2. LOTO Release if applicable */}
                {permit.lotoSteps && permit.lotoSteps.length > 0 && (
                    <div className="space-y-4">
                        <div className="text-[10px] text-red-500 uppercase font-black tracking-widest pl-1">LOTO Vrijgave (Sloten Verwijderen)</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {permit.lotoSteps.map((step, idx) => {
                                const key = `loto_remove_${idx}`;
                                const isChecked = !!completionChecks[key];
                                return (
                                    <label key={idx} className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all shadow-lg cursor-pointer ${isChecked ? 'bg-red-500/10 border-red-500/30' : 'bg-slate-800/50 border-slate-700 opacity-60'
                                        }`}>
                                        <input
                                            type="checkbox"
                                            className="w-6 h-6 rounded border-slate-500 text-red-600 bg-slate-950 mt-1 cursor-pointer shrink-0"
                                            checked={isChecked}
                                            onChange={() => handleCompletionCheck(key)}
                                        />
                                        <div className="flex-1">
                                            <span className="text-sm font-bold text-white block leading-snug">{step.step}</span>
                                            <span className="text-[10px] text-slate-500 block italic leading-tight font-medium mt-1">{step.instruction}</span>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* 3. Condition Check */}
                <div className="space-y-4">
                    <div className="text-[10px] text-blue-500 uppercase font-black tracking-widest pl-1">Conditie Check (NEN 2767)</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                        {[
                            { s: 1, l: 'Excellent', hex: '#22c55e' },
                            { s: 2, l: 'Goed', hex: '#84cc16' },
                            { s: 3, l: 'Redelijk', hex: '#eab308' },
                            { s: 4, l: 'Matig', hex: '#f97316' },
                            { s: 5, l: 'Slecht', hex: '#ef4444' },
                            { s: 6, l: 'Sloop', hex: '#b91c1c' }
                        ].map(item => {
                            const isActive = Number(conditionScore) === item.s;
                            return (
                                <button
                                    key={item.s}
                                    onClick={() => setConditionScore(item.s)}
                                    className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all shadow-lg ${isActive ? 'border-white/50 text-black font-black scale-105 shadow-xl' : 'bg-slate-800/50 border-slate-700 text-slate-300'
                                        }`}
                                    style={isActive ? { backgroundColor: item.hex } : {}}
                                >
                                    <span className="text-xl leading-none font-black">{item.s}</span>
                                    <span className="text-[9px] uppercase font-black mt-1">{item.l}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* 3.5 Asset Replacement Verification (Conditional) */}
                {permit.taskMetadata?.isReplacement && (
                    <div className="space-y-4 animate-slide-up">
                        <div className="text-[10px] text-emerald-500 uppercase font-black tracking-widest pl-1">🔄 Asset Vervanging Verificatie</div>
                        <div className="bg-emerald-600/10 border-2 border-emerald-500/30 rounded-2xl p-6 shadow-xl ring-1 ring-emerald-500/20">
                            <h4 className="text-white font-black text-lg mb-2 uppercase italic tracking-tighter">Is het onderdeel daadwerkelijk vervangen?</h4>
                            <p className="text-emerald-400/80 text-xs leading-relaxed mb-6 font-medium">
                                Bij bevestiging wordt het <span className="text-white font-bold">Bouwjaar naar {new Date().getFullYear()}</span> gezet en de conditie automatisch op <span className="text-white font-bold">Nieuwstaat</span>.
                            </p>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => setIsReplacementConfirmed(true)}
                                    data-testid="replacement-confirm-ja"
                                    className={`flex-1 py-4 rounded-xl text-lg font-black transition-all border-2 flex items-center justify-center gap-2 ${isReplacementConfirmed
                                        ? 'bg-emerald-500 text-black border-white/30 scale-[1.02] shadow-[0_0_30px_rgba(16,185,129,0.3)]'
                                        : 'bg-slate-800 text-slate-400 border-slate-700 opacity-60 hover:opacity-100'}`}
                                >
                                    <span>✅</span> JA
                                </button>
                                <button
                                    onClick={() => setIsReplacementConfirmed(false)}
                                    data-testid="replacement-confirm-nee"
                                    className={`flex-1 py-4 rounded-xl text-lg font-black transition-all border-2 flex items-center justify-center gap-2 ${!isReplacementConfirmed
                                        ? 'bg-red-500 text-white border-white/30 scale-[1.02] shadow-[0_0_30px_rgba(239,68,68,0.3)]'
                                        : 'bg-slate-800 text-slate-400 border-slate-700 opacity-60 hover:opacity-100'}`}
                                >
                                    <span>❌</span> NEE
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 4. ISO 142 registratie - Storingsmelding */}
                <div className="space-y-4 mt-6">
                    <div className="text-[10px] text-red-400 uppercase font-black tracking-widest pl-1">⚠️ Storingsregistratie</div>
                    <div className="bg-slate-800/80 p-5 rounded-xl border-2 border-red-500/30 shadow-xl ring-1 ring-red-500/10">
                        <label className="flex items-center gap-4 cursor-pointer">
                            <input
                                type="checkbox"
                                className="w-6 h-6 accent-red-500 flex-shrink-0"
                                checked={completionFeedback.isFailure || false}
                                onChange={(e) => setCompletionFeedback(prev => ({ ...prev, isFailure: e.target.checked }))}
                            />
                            <span className="text-sm font-black text-white uppercase tracking-tight">Was er sprake van een defect/storing?</span>
                        </label>
                    </div>

                    {completionFeedback.isFailure && (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in pt-2">
                                {[
                                    { label: 'Component', key: 'isoComponent', libKey: 'components' },
                                    { label: 'Failure Mode', key: 'isoMode', libKey: 'modes' }
                                ].map(field => {
                                    // Extract smart library context
                                    const context = (permit.component || permit.title || '').toLowerCase();
                                    let detectedKey = 'general';

                                    if (context.includes('motor')) detectedKey = 'electric_motor';
                                    else if (context.includes('pomp')) detectedKey = 'pump';
                                    else if (context.includes('klep') || context.includes('ventiel')) detectedKey = 'valve';
                                    else if (context.includes('band') || context.includes('conveyor') || context.includes('rol')) detectedKey = 'conveyor';
                                    else if (context.includes('sensor') || context.includes('cel') || context.includes('encoder')) detectedKey = 'sensor';
                                    else if (context.includes('compressor')) detectedKey = 'compressor';
                                    else if (context.includes('wisselaar')) detectedKey = 'heat_exchanger';

                                    const libraryData = isoLibrary[detectedKey] || isoLibrary['general'] || { components: [], modes: [] };

                                    return (
                                        <div key={field.key}>
                                            <label className="text-[10px] text-slate-500 uppercase font-black mb-1.5 block tracking-wider pl-1">
                                                {field.key === 'isoComponent' ? 'Defect Onderdeel' : field.label}
                                            </label>
                                            <select
                                                className="w-full bg-white border-2 border-slate-300 rounded-xl p-3 text-sm text-black font-black focus:border-red-500 outline-none transition-all"
                                                value={completionFeedback[field.key] || ''}
                                                onChange={(e) => setCompletionFeedback(prev => ({ ...prev, [field.key]: e.target.value }))}
                                            >
                                                <option value="">-- Kies --</option>
                                                {libraryData[field.libKey]?.map((opt, i) => (
                                                    <option key={i} value={opt}>{isoTranslationMap[opt] || opt}</option>
                                                ))}
                                            </select>
                                        </div>
                                    );
                                })}
                                <div>
                                    <label className="text-[10px] text-slate-500 uppercase font-black mb-1.5 block tracking-wider pl-1">Oorzaak</label>
                                    <select className="w-full bg-white border-2 border-slate-300 rounded-xl p-3 text-sm text-black font-black focus:border-red-500 outline-none transition-all" value={completionFeedback.isoCause || ''} onChange={(e) => setCompletionFeedback(prev => ({ ...prev, isoCause: e.target.value }))}>
                                        <option value="">-- Kies --</option>
                                        <option value="Wear">Normale slijtage</option>
                                        <option value="Lubrication">Smering</option>
                                        <option value="Misalignment">Uitlijning</option>
                                        <option value="Overload">Overbelasting</option>
                                        <option value="Unknown">Onbekend</option>
                                    </select>
                                </div>
                            </div>

                            <div className="mt-4 p-3 bg-red-500/5 rounded-lg border border-red-500/10">
                                <p className="text-[10px] text-red-400/80 leading-relaxed italic font-medium">
                                    💡 <b>Tip:</b> Kies hierboven het specifieke onderdeel <i>binnen</i> de asset dat het defect veroorzaakte.
                                </p>
                            </div>
                        </>
                    )}

                    {completionFeedback.isFailure && (
                        <div className="space-y-4 pt-4 border-t border-slate-700/50 animate-fade-in">
                            <div className="bg-slate-800/80 p-5 rounded-xl border-2 border-slate-700 shadow-xl ring-1 ring-white/5">
                                <div className="text-sm font-black text-white uppercase tracking-tight mb-4">Was er een storingscode zichtbaar?</div>
                                <div className="flex gap-3">
                                    {(() => {
                                        const isYes = completionFeedback.hasFaultCode === 'yes';
                                        const isNo = completionFeedback.hasFaultCode === 'no';
                                        return (
                                            <>
                                                <button
                                                    onClick={() => setCompletionFeedback(prev => ({ ...prev, hasFaultCode: 'yes' }))}
                                                    className={`flex-1 px-8 py-4 rounded-xl text-lg font-black transition-all shadow-lg border-2 ${isYes
                                                        ? 'border-white/50 text-black scale-105 shadow-xl'
                                                        : 'bg-white text-black border-slate-300 hover:bg-slate-50'}`}
                                                    style={isYes ? { backgroundColor: '#22c55e' } : {}}
                                                >
                                                    JA
                                                </button>
                                                <button
                                                    onClick={() => setCompletionFeedback(prev => ({ ...prev, hasFaultCode: 'no' }))}
                                                    className={`flex-1 px-8 py-4 rounded-xl text-lg font-black transition-all shadow-lg border-2 ${isNo
                                                        ? 'border-white/50 text-white scale-105 shadow-xl'
                                                        : 'bg-white text-black border-slate-300 hover:bg-slate-50'}`}
                                                    style={isNo ? { backgroundColor: '#ef4444' } : {}}
                                                >
                                                    NEE
                                                </button>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>

                            {completionFeedback.hasFaultCode === 'yes' && (
                                <div className="space-y-4 animate-fade-in pt-4 border-t border-slate-700/50">
                                    <div className="text-sm font-black text-white uppercase tracking-tight mb-4">Heb je een foto gemaakt?</div>
                                    <div className="flex gap-3 mb-4">
                                        {(() => {
                                            const isYes = completionFeedback.hasFaultPhoto === 'yes';
                                            const isNo = completionFeedback.hasFaultPhoto === 'no';
                                            return (
                                                <>
                                                    <button
                                                        onClick={() => setCompletionFeedback(prev => ({ ...prev, hasFaultPhoto: 'yes' }))}
                                                        className={`flex-1 px-8 py-4 rounded-xl text-lg font-black transition-all shadow-lg border-2 ${isYes
                                                            ? 'border-white/50 text-black scale-105 shadow-xl'
                                                            : 'bg-white text-black border-slate-300 hover:bg-slate-50'}`}
                                                        style={isYes ? { backgroundColor: '#22c55e' } : {}}
                                                    >
                                                        JA
                                                    </button>
                                                    <button
                                                        onClick={() => setCompletionFeedback(prev => ({ ...prev, hasFaultPhoto: 'no' }))}
                                                        className={`flex-1 px-8 py-4 rounded-xl text-lg font-black transition-all shadow-lg border-2 ${isNo
                                                            ? 'border-white/50 text-white scale-105 shadow-xl'
                                                            : 'bg-white text-black border-slate-300 hover:bg-slate-50'}`}
                                                        style={isNo ? { backgroundColor: '#ef4444' } : {}}
                                                    >
                                                        NEE
                                                    </button>
                                                </>
                                            );
                                        })()}
                                    </div>

                                    {completionFeedback.hasFaultPhoto === 'yes' && (
                                        <div className="bg-slate-800 p-4 rounded-xl border border-blue-500/30 animate-fade-in">
                                            <ImageUpload
                                                path="fault_codes"
                                                compact={true}
                                                label="📸 STORINGSCODE FOTO"
                                                currentImageUrls={completionFeedback.faultCodePhotos || []}
                                                selectedUrls={completionFeedback.faultCodePhotos || []}
                                                sessionUploadedUrls={completionFeedback.faultCodePhotos || []}
                                                allowDelete={true}
                                                onUploadComplete={(urls) => {
                                                    setCompletionFeedback(prev => ({ ...prev, faultCodePhotos: [...(prev.faultCodePhotos || []), ...urls] }));
                                                    // Track for session cleanup
                                                    if (onExecutionUpload) urls.forEach(url => onExecutionUpload(url));
                                                }}
                                                onToggleSelect={(url) => setCompletionFeedback(prev => ({
                                                    ...prev,
                                                    faultCodePhotos: (prev.faultCodePhotos || []).filter(u => u !== url)
                                                }))}
                                                onSessionDelete={(url) => setCompletionFeedback(prev => ({
                                                    ...prev,
                                                    faultCodePhotos: (prev.faultCodePhotos || []).filter(u => u !== url)
                                                }))}
                                            />
                                        </div>
                                    )}

                                    {completionFeedback.hasFaultPhoto === 'no' && (
                                        <div className="space-y-4 animate-fade-in pt-4 border-t border-slate-700/50">
                                            <div className="space-y-2">
                                                <label className="text-[10px] text-slate-500 uppercase font-black mb-1 block tracking-wider">Waarom niet?</label>
                                                <select
                                                    className="w-full bg-slate-900 border-slate-700 rounded p-3 text-sm text-white focus:border-red-500"
                                                    value={completionFeedback.faultPhotoMissingReason || ''}
                                                    onChange={(e) => setCompletionFeedback(prev => ({ ...prev, faultPhotoMissingReason: e.target.value }))}
                                                >
                                                    <option value="">-- Kies een reden --</option>
                                                    <option value="no_camera">Geen camera beschikbaar</option>
                                                    <option value="code_disappeared">Code was direct weer verdwenen</option>
                                                    <option value="forgotten">Vergeten</option>
                                                    <option value="other">Anders, namelijk...</option>
                                                </select>
                                            </div>
                                            <VoiceInput
                                                value={completionFeedback.faultPhotoMissingDetail || ''}
                                                onChange={(e) => setCompletionFeedback(prev => ({ ...prev, faultPhotoMissingDetail: e.target.value }))}
                                                placeholder="Extra toelichting..."
                                                className="w-full bg-white border border-slate-300 rounded-lg p-3 text-sm text-black font-medium"
                                                minHeight="h-20"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* 4.5 Tech Doc Kwaliteit */}
                <div className="space-y-4 mt-6">
                    <div className="text-[10px] text-yellow-400 uppercase font-black tracking-widest pl-1">📚 Tech Doc Kwaliteit</div>
                    <div className="bg-slate-800/80 p-5 rounded-xl border-2 border-yellow-500/30 shadow-xl ring-1 ring-yellow-500/10 space-y-4">
                        {/* Tag/Label Issue */}
                        <div className="space-y-2">
                            <label className="flex items-center gap-4 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="w-6 h-6 accent-yellow-500 flex-shrink-0"
                                    checked={completionFeedback.hasTagLabelIssue || false}
                                    onChange={(e) => setCompletionFeedback(prev => ({ ...prev, hasTagLabelIssue: e.target.checked }))}
                                />
                                <span className="text-sm font-black text-white uppercase tracking-tight">Tag/Label afwijking geconstateerd</span>
                            </label>
                            {completionFeedback.hasTagLabelIssue && (
                                <LocalTextarea
                                    placeholder="Welke tag? Wat klopt niet? (bijv. 'V-101 ontbreekt op apparaat')"
                                    value={completionFeedback.tagLabelDetails || ''}
                                    onChange={(val) => setCompletionFeedback(prev => ({ ...prev, tagLabelDetails: val }))}
                                    className="w-full bg-slate-900 text-white border border-yellow-500/50 rounded-lg p-3 text-sm focus:border-yellow-400 focus:outline-none resize-none"
                                    rows={2}
                                />
                            )}
                        </div>

                        {/* Tekenpakket Issue */}
                        <div className="space-y-2">
                            <label className="flex items-center gap-4 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="w-6 h-6 accent-yellow-500 flex-shrink-0"
                                    checked={completionFeedback.hasTekenpakketIssue || false}
                                    onChange={(e) => setCompletionFeedback(prev => ({ ...prev, hasTekenpakketIssue: e.target.checked }))}
                                />
                                <span className="text-sm font-black text-white uppercase tracking-tight">Tekenpakket/P&ID afwijking geconstateerd</span>
                            </label>
                            {completionFeedback.hasTekenpakketIssue && (
                                <LocalTextarea
                                    placeholder="Welk document? Wat klopt niet? (bijv. 'Leiding L-45 ontbreekt op P&ID-003')"
                                    value={completionFeedback.tekenpakketDetails || ''}
                                    onChange={(val) => setCompletionFeedback(prev => ({ ...prev, tekenpakketDetails: val }))}
                                    className="w-full bg-slate-900 text-white border border-yellow-500/50 rounded-lg p-3 text-sm focus:border-yellow-400 focus:outline-none resize-none"
                                    rows={2}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* 5. Slimme Terugkoppeling */}
                <div className="bg-blue-600/10 p-4 rounded-xl border border-blue-500/30">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                            <span className="text-xl">🎙️</span>
                            <div>
                                <div className="text-sm font-bold text-white leading-none">Slimme Terugkoppeling</div>
                                <div className="text-[10px] text-blue-400 font-bold uppercase tracking-widest mt-1">Audio Toelichting & Analyse</div>
                            </div>
                        </div>
                        <div className="px-2 py-1 rounded bg-red-500/10 border border-red-500/20">
                            <span className="text-[9px] font-black text-red-500 uppercase tracking-tighter">Verplicht</span>
                        </div>
                    </div>
                    <VoiceInput
                        value={completionFeedback.feedbackText || (typeof completionFeedback === 'string' ? completionFeedback : '')}
                        onChange={(e) => setCompletionFeedback(prev => ({ ...prev, feedbackText: e.target.value }))}
                        placeholder="Vertel kort wat je hebt gedaan..."
                        className="w-full bg-white border border-blue-500/20 rounded-lg p-3 text-sm text-black focus:border-blue-500 transition-all shadow-inner"
                        minHeight="h-24"
                    />

                    {/* ISO Auto-fill confirmation badge */}
                    {isoAnalysis && isoAnalysis.confidence > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2 animate-fade-in">
                            <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider self-center mr-1">Auto-detect:</span>
                            {isoAnalysis.matchDetails.component && (
                                <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                    🧩 {isoAnalysis.component}
                                </span>
                            )}
                            {isoAnalysis.matchDetails.failureMode && (
                                <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
                                    💥 {isoAnalysis.failureMode}
                                </span>
                            )}
                            {isoAnalysis.matchDetails.cause && (
                                <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                    🔍 {isoAnalysis.cause}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Vocabulary Learner */}
                    {showLearner && isoAnalysis?.unmatchedWords?.length > 0 && vocabulary && (
                        <VocabularyLearner
                            unmatchedWords={isoAnalysis.unmatchedWords}
                            vocabulary={vocabulary}
                            onLearn={handleLearnWord}
                            onDismiss={() => setShowLearner(false)}
                        />
                    )}
                </div>

                {/* Footer Actions */}
                <div className="flex flex-wrap items-center gap-3 pt-6 border-t border-slate-700/50">
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="btn btn-sm bg-slate-700 text-slate-200 hover:bg-slate-600 border-slate-600 text-[10px] uppercase font-black"
                        >
                            Annuleren
                        </button>
                    )}

                    {/* Export Buttons */}
                    <button
                        onClick={handleExportPDF}
                        disabled={exporting === 'pdf'}
                        className="btn btn-sm bg-white text-slate-900 border-none shadow-md hover:bg-slate-100 font-bold text-[10px] uppercase disabled:opacity-50 disabled:cursor-wait flex items-center gap-2"
                    >
                        📄 {exporting === 'pdf' ? 'Bezig...' : 'PDF Export'}
                    </button>
                    <button
                        onClick={handleExportCMMS}
                        disabled={exporting === 'cmms'}
                        className="btn btn-sm bg-white text-slate-900 border-none shadow-md hover:bg-slate-100 font-bold text-[10px] uppercase disabled:opacity-50 disabled:cursor-wait flex items-center gap-2"
                    >
                        📤 {exporting === 'cmms' ? 'Bezig...' : 'CMMS Export'}
                    </button>

                    {/* Main Complete Button */}
                    <button
                        onClick={onComplete}
                        disabled={!canComplete}
                        className={`btn btn-lg flex-1 py-4 text-sm font-black uppercase tracking-widest transition-all duration-300 shadow-lg ${canComplete
                            ? 'bg-yellow-400 text-black hover:bg-yellow-300 shadow-[0_0_20px_rgba(250,204,21,0.3)] scale-[1.02] animate-pulse-glow'
                            : 'bg-slate-800 text-slate-500 border-slate-700 cursor-not-allowed'
                            }`}
                    >
                        {canComplete ? '✓ Definitief Afronden' : (canCompleteReason || 'Vul toelichting in...')}
                    </button>
                </div>
            </div>
        </AccordionSection >
    );
}
